import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { Articulo, Equivalencia, Inventario } from './articulo';

export interface ArticulosState {
  list: PaginatedList<Articulo>;
  current: Articulo;
  actives: Articulo[];

  nuevo: () => void;
  cargarPaginados: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivos: () => Promise<void>;
  cargarPorId: (id: string) => Promise<void>;
  registrar: (request: RegistrarArticulo) => Promise<boolean>;
  actualizar: (id: string, request: ActualizarArticulo) => Promise<boolean>;
  activar: (id: string) => Promise<boolean>;
  desactivar: (id: string) => Promise<boolean>;
}

export interface RegistrarArticulo {
  id: string;
  idClasificacion: number;
  descripcion: string;
  idAgrupaciones: Array<number>;
  equivalencias: Array<Equivalencia>;
  ubicacionesAlmacen: Array<Inventario>;
}

export interface ActualizarArticulo {
  idClasificacion: number;
  descripcion: string;
  idAgrupaciones: Array<number>;
  equivalencias: Array<Equivalencia>;
  ubicacionesAlmacen: Array<Inventario>;
}

const { showSuccessToast } = useUIStore.getState();

export const useArticulosStore = create<ArticulosState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as Articulo,
  actives: [],

  nuevo: () =>
    set({
      current: {} as Articulo
    }),
  cargarPaginados: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<Articulo>>(
      `/articulos/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivos: async () => {
    const data = await makeGet<Array<Articulo>>('/articulos/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: string) => {
    const data = await makeGet<Articulo>(`/articulos/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarArticulo) => {
    const data = await makePost<RegistrarArticulo, Articulo>('/articulos/', request);

    if (data.isFailure) return false;

    showSuccessToast('Registro agregado');

    set({
      current: {} as Articulo
    });

    return true;
  },
  actualizar: async (id: string, request: ActualizarArticulo) => {
    const data = await makePatch<ActualizarArticulo, Articulo>(`/articulos/${id}`, request);

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: string) => {
    const data = await makePatch(`/articulos/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activo: true
      }
    });

    return true;
  },
  desactivar: async (id: string) => {
    const data = await makePatch(`/articulos/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activo: false
      }
    });

    return true;
  }
}));
